import { cva, type VariantProps } from 'class-variance-authority';
import { AlertCircle, CircleCheck, Info, LoaderCircle, TriangleAlert, WifiOff } from 'lucide-react';
import { useId, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from './cn.js';

export function Panel({ className, ...props }: HTMLAttributes<HTMLElement>) {
  return (
    <section
      className={cn('rounded-lg border border-border bg-surface p-5 shadow-sm', className)}
      {...props}
    />
  );
}

const bannerVariants = cva('flex items-start gap-3 rounded-md border px-4 py-3 text-sm', {
  variants: {
    tone: {
      info: 'border-border-hi bg-surface text-ink-2',
      success:
        'border-[color-mix(in_oklch,var(--ok)_40%,var(--border))] bg-[color-mix(in_oklch,var(--ok)_10%,transparent)] text-ink',
      warning:
        'border-[color-mix(in_oklch,var(--warn)_40%,var(--border))] bg-[color-mix(in_oklch,var(--warn)_10%,transparent)] text-ink',
      danger:
        'border-[color-mix(in_oklch,var(--err)_40%,var(--border))] bg-[color-mix(in_oklch,var(--err)_10%,transparent)] text-ink',
    },
  },
  defaultVariants: { tone: 'info' },
});

const bannerIcons = {
  info: Info,
  success: CircleCheck,
  warning: TriangleAlert,
  danger: AlertCircle,
};

export interface BannerProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'title'>, VariantProps<typeof bannerVariants> {
  action?: ReactNode;
  icon?: ReactNode;
  title?: ReactNode;
}

export function Banner({
  action,
  children,
  className,
  icon,
  title,
  tone = 'info',
  ...props
}: BannerProps) {
  const Icon = bannerIcons[tone ?? 'info'];
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn(bannerVariants({ tone }), className)}
      {...props}
    >
      {icon ?? <Icon aria-hidden="true" className="mt-0.5 size-4 shrink-0" />}
      <div className="min-w-0 flex-1">
        {title && <div className="font-semibold text-ink">{title}</div>}
        {children && <div className={cn('leading-5', title && 'mt-1 text-ink-2')}>{children}</div>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export type StateKind = 'empty' | 'loading' | 'error' | 'offline' | 'action-required';

const stateSurfaceVariants = cva(
  'grid min-h-48 place-items-center rounded-lg border px-6 py-10 text-center',
  {
    variants: {
      kind: {
        empty: 'border-dashed border-border-hi bg-bg-2',
        loading: 'border-dashed border-border-hi bg-bg-2',
        error:
          'border-[color-mix(in_oklch,var(--err)_40%,var(--border))] bg-[color-mix(in_oklch,var(--err)_8%,var(--bg-2))]',
        offline: 'border-border-hi bg-surface',
        'action-required':
          'border-[color-mix(in_oklch,var(--warn)_45%,var(--border))] bg-[color-mix(in_oklch,var(--warn)_9%,var(--bg-2))]',
      },
    },
    defaultVariants: { kind: 'empty' },
  },
);

const stateIcons = {
  empty: Info,
  loading: LoaderCircle,
  error: AlertCircle,
  offline: WifiOff,
  'action-required': TriangleAlert,
} satisfies Record<StateKind, typeof Info>;

const stateIconClasses: Record<StateKind, string> = {
  empty: 'text-ink-3',
  loading: 'animate-spin text-ink-3',
  error: 'text-err',
  offline: 'text-ink-2',
  'action-required': 'text-warn',
};

export function StateSurface({
  action,
  children,
  className,
  kind = 'empty',
  title,
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  action?: ReactNode;
  kind?: StateKind;
  title: ReactNode;
}) {
  const titleId = useId();
  const descriptionId = useId();
  const isLoading = kind === 'loading';
  const isAlert = kind === 'error' || kind === 'action-required';
  const Icon = stateIcons[kind];
  return (
    <div
      role={isAlert ? 'alert' : 'status'}
      aria-atomic="true"
      aria-busy={isLoading || undefined}
      aria-describedby={children ? descriptionId : undefined}
      aria-labelledby={titleId}
      aria-live={isAlert ? 'assertive' : 'polite'}
      data-kind={kind}
      className={cn(stateSurfaceVariants({ kind }), className)}
      {...props}
    >
      <div className="grid max-w-md justify-items-center gap-3">
        <Icon aria-hidden="true" className={cn('size-5', stateIconClasses[kind])} />
        <div>
          <div id={titleId} className="font-semibold text-ink">
            {title}
          </div>
          {children && (
            <div id={descriptionId} className="mt-1 text-sm leading-6 text-ink-3">
              {children}
            </div>
          )}
        </div>
        {action && <div className="mt-1 flex min-h-11 items-center justify-center">{action}</div>}
      </div>
    </div>
  );
}
